import React, { useMemo } from 'react';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import useFeatureFlagsQuery from 'common/Hooks/UseFeatureFlagsQuery';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { Venue } from 'generated/graphql';
import styles from './FeatureTiles.module.scss';
import FeatureTile from './FeatureTile';

interface Props {
  currentVenue?: Venue;
}

const { ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES, ENABLE_OPERATING_HOURS_FOR_VENUES_WITH_SKIP, SKIP_ID } =
  getEnvironmentVariables();

export interface Feature {
  icon: string;
  heading: string;
  subheading: string;
  link: string;
  featureFlag?: string;
  new?: boolean;
}

const features: Feature[] = [
  {
    icon: 'mm-percentage',
    heading: 'Dynamic pricing',
    subheading: 'Temporarily increase your menu prices',
    link: 'pricing',
    featureFlag: 'surchargeEnabled'
  },
  {
    icon: 'mm-pause',
    heading: 'Busy mode',
    subheading: 'Snooze your app menus when busy',
    link: 'busy',
    featureFlag: 'pauseStoreEnabled'
  },
  {
    icon: 'mm-clock',
    heading: 'Menu hours & dates',
    subheading: 'Set the days / hours in your app menus',
    link: 'hours',
    featureFlag: 'operatingHoursEnabled',
    new: true
  }
];

export const FeatureTiles: React.FC<Props> = ({ currentVenue }) => {
  const [data, loading, error] = useFeatureFlagsQuery();

  const venueHasVirtualBrand = currentVenue?.menus?.some((menu) => menu.apps?.some((app) => app.brand));

  const venueHasSkipAppConnection = currentVenue?.menus?.some((menu) =>
    menu.apps?.some((app) => Number(app.id) === SKIP_ID)
  );

  const enabledFeatures = useMemo(() => {
    if (loading || error || !data) {
      return [];
    }

    return features.filter((feature) => {
      if (feature.featureFlag) {
        const featureFlag = data[feature.featureFlag as string];

        if (feature.featureFlag === 'operatingHoursEnabled') {
          if (!ENABLE_OPERATING_HOURS_FOR_VENUES_WITH_SKIP && venueHasSkipAppConnection) {
            return false;
          }
          if (venueHasVirtualBrand && !ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES) {
            return false;
          }
        }
        return featureFlag ? featureFlag.isEnabled : false;
      }
      return true;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <section
      className={`${styles.Outer} container`}
      data-testid="featureTiles-container"
      aria-labelledby="feature-tiles-heading"
    >
      <ul className={styles.List}>
        {enabledFeatures.map((feature) => (
          <FeatureTile
            icon={feature.icon}
            heading={feature.heading}
            key={feature.heading}
            subheading={feature.subheading}
            link={feature.link}
            isNew={feature.new}
            handleOnClick={() =>
              logAmplitudeEvent({
                name: `click ${feature.heading}`,
                category: AmplitudeEventCategories.MM_ALL_ITEMS,
                properties: {
                  [AmplitudeEventProperties.VENUE_ID]: currentVenue?.hashedId || ''
                }
              })
            }
          />
        ))}
      </ul>
    </section>
  );
};
